/**
 * A 2D Matrix
 *
 * Created: 2016-08-09
 * @author jonas
 */

export class StateMatrix<T extends { toString():string }> {
    get states():T[] {
        return this._states;
    }

    get symbols():number {
        return this._symbols;
    }

    get reels():number {
        return this._reels;
    }

    private readonly _reels:number;
    private readonly _symbols:number;
    private readonly _visibleSymbolsOffset:number;
    private readonly _visibleSymbolsLength:number;
    private _stateMatrix:T[][];
    private readonly _states:T[];

    /**
     * SymbolStateMatrix constructor.
     *
     * @param reels Number of reels
     * @param symbols Total number of symbols in each reel
     * @param visibleRange Start index to end index of visible range, inclusive.
     * @param initState T state that will be used to initialize the matrix.
     */
    constructor(reels:number, symbols:number, visibleRange:[number, number], initState:T) {
        this._reels = reels;
        this._symbols = symbols;
        this._visibleSymbolsOffset = visibleRange[0];
        this._visibleSymbolsLength = visibleRange[1] - visibleRange[0] + 1;
        this._stateMatrix = [];
        this._states = [];
        for(let i:number = 0; i < this._reels; i++) {
            this._stateMatrix.push(new Array(this._symbols));
        }
        this.populate(initState)
    }

    private addStateToList(newState:T):void {
        this.cleanupUnusedStates();
        for(let state of this._states) {
            if(state == newState) {
                return;
            }
        }
        this._states.push(newState);

    }

    private cleanupUnusedStates():void {
        for(let i:number = this._states.length - 1; i >= 0; i--) {
            const state:T = this._states[i];
            if(this.getAllCoordinatesWithState(state).length <= 0) {
                this._states.splice(i, 1);
            }
        }
    }

    /**
     * Initiates the state matrix with an matching matrix.
     *
     * @param states An already populated matching state matrix.
     */
    public initAbsolute(states:T[][]) {
        for(let reelIndex = 0; reelIndex < states.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < states[reelIndex].length; symbolIndex++) {
                this.setStateAtAbsolute(reelIndex, symbolIndex, states[reelIndex][symbolIndex]);
            }
        }
    }

    /**
     * Initiates the state matrix with an matching matrix.
     *
     * @param states An already populated matching state matrix.
     */
    public init(states:T[][]) {
        for(let reelIndex = 0; reelIndex < states.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < states[reelIndex].length; symbolIndex++) {
                this.setStateAt(reelIndex, symbolIndex, states[reelIndex][symbolIndex]);
            }
        }
    }

    /**
     * Populates entire matrix with the same state. Useful for resetting.
     *
     * @param state
     */
    public populate(state:T) {
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                this.setStateAtAbsolute(reelIndex, symbolIndex, state);
            }
        }
    }

    public swapState(fromState:T, toState:T) {
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                let state = this.getStateAtAbsolute(reelIndex, symbolIndex);
                if(state == fromState) {
                    this.setStateAtAbsolute(reelIndex, symbolIndex, toState);
                }
            }
        }
    }


    /**
     * Sets state at coordinates without taking "visibleSymbolsOffset" into account.
     * So no negative indices!
     *
     * @param reelIndex Zero based reelIndex
     * @param symbolIndex Zero based symbolIndex
     * @param state The state to set
     */
    public setStateAtAbsolute(reelIndex:number, symbolIndex:number, state:T):void {
        this._stateMatrix[reelIndex][symbolIndex] = state;
        this.addStateToList(state);
    }

    /**
     * Sets state at coordinates with "visibleSymbolsOffset" taken into account.
     *
     * So you can set symbols with a negative index.
     * If the absolute coordinate is 0,0 then you would set it with 0,-2 with this function.
     *
     * @param reelIndex
     * @param symbolIndex
     * @param state
     */
    public setStateAt(reelIndex:number, symbolIndex:number, state:T):void {
        this.setStateAtAbsolute(reelIndex, symbolIndex + this._visibleSymbolsOffset, state);
    }

    public setStatesAt(coordinates:[number, number][], state:T):void {
        for(let i:number = 0; i < coordinates.length; i++) {
            this.setStateAt(coordinates[i][0], coordinates[i][1], state);
        }
    }

    public setStatesInReel(reelIndex:number, state:T):void {
        for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
            this.setStateAt(reelIndex, symbolIndex, state);

        }
    }

    /**
     * Returns state at coordinates without taking "visibleSymbolsOffset" into account.
     * So no negative indices!
     *
     * @param reelIndex Zero based reelIndex
     * @param symbolIndex Zero based symbolIndex
     * @returns {string} State
     */
    public getStateAtAbsolute(reelIndex:number, symbolIndex:number):T {
        return this._stateMatrix[reelIndex][symbolIndex];
    }


    /**
     * Returns state at coordinates with "visibleSymbolsOffset" taken into account.
     *
     * So you can get symbols with a negative index.
     * If the absolute coordinate is 0,0 then you would get it with 0,-2 with this function.
     *
     * @param reelIndex ReelIndex
     * @param symbolIndex SymbolIndex
     * @returns {string} State
     */
    public getStateAt(reelIndex:number, symbolIndex:number):T {
        return this.getStateAtAbsolute(reelIndex, symbolIndex + this._visibleSymbolsOffset);
    }

    public getStatesAt(coordinates:[number, number][], state:T):T[] {
        const states:T[] = [];
        for(let i:number = 0; i < coordinates.length; i++) {
            states.push(this.getStateAt(coordinates[i][0], coordinates[i][1]));
        }
        return states;
    }

    public getStatesInReel(reelIndex:number):T[] {
        const states:T[] = [];
        for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
            states.push(this.getStateAt(reelIndex, symbolIndex));

        }
        return states;
    }


    public getCoordinatesWithStateInReel(reelIndex:number, state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
            if(this.getStateAtAbsolute(reelIndex, symbolIndex) == state) {
                result.push([reelIndex, symbolIndex - this._visibleSymbolsOffset]);
            }
        }
        return result;
    }

    /**
     * Gets all matching state coordinates and returns them in a array of [number,number] tuples.
     *
     * @param state State to filter.
     * @returns {[number,number][]} Array of [number,number] Coordinate tuples for all matching states.
     */
    public getAllCoordinatesWithState(state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                if(this.getStateAtAbsolute(reelIndex, symbolIndex) == state) {
                    result.push([reelIndex, symbolIndex - this._visibleSymbolsOffset]);
                }
            }
        }
        return result;
    }

    /**
     * Gets all state coordinates except the one specified
     *
     * @param state State to filter.
     * @returns {[number,number][]} Array of [number,number] Coordinate tuples for all matching states.
     */
    public getAllCoordinatesExceptState(state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                if(this.getStateAtAbsolute(reelIndex, symbolIndex) != state) {
                    result.push([reelIndex, symbolIndex - this._visibleSymbolsOffset]);
                }
            }
        }
        return result;
    }

    /**
     * Gets all state coordinates except the one specified
     *
     * @param states States to filter.
     * @returns {[number,number][]} Array of [number,number] Coordinate tuples for all matching states.
     */
    public getAllCoordinatesExceptStates(states:T[]):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                let currentState = this.getStateAtAbsolute(reelIndex, symbolIndex);
                let found = false;
                for(let state of states) {
                    if(currentState == state) {
                        found = true;
                        break;
                    }
                }

                if(found == false) {
                    result.push([reelIndex, symbolIndex - this._visibleSymbolsOffset]);
                }

            }
        }
        return result;
    }

    public getAllCoordinates():[number, number][] {
        let result:[number, number][] = [];

        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                result.push([reelIndex, symbolIndex]);
            }
        }
        return result;
    }

    public getVisibleCoordinatesWithState(state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._visibleSymbolsLength; symbolIndex++) {
                if(this.getStateAt(reelIndex, symbolIndex) == state) {
                    result.push([reelIndex, symbolIndex]);
                }
            }
        }
        return result;
    }

    public setVisibleCoordinatesWithState(state:T):void {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._visibleSymbolsLength; symbolIndex++) {
                this.setStateAt(reelIndex, symbolIndex, state)
            }
        }
    }

    public getCoordinatesWithStateBeforeVisible(state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = 0; symbolIndex < this._visibleSymbolsOffset; symbolIndex++) {
                if(this.getStateAtAbsolute(reelIndex, symbolIndex) == state) {
                    result.push([reelIndex, symbolIndex - this._visibleSymbolsOffset]);
                }
            }
        }
        return result;
    }

    public getCoordinatesWithStateAfterVisible(state:T):[number, number][] {
        let result:[number, number][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            for(let symbolIndex = this._visibleSymbolsOffset + this._visibleSymbolsLength; symbolIndex < this._stateMatrix[reelIndex].length; symbolIndex++) {
                if(this.getStateAtAbsolute(reelIndex, symbolIndex) == state) {
                    result.push([reelIndex, symbolIndex - this._visibleSymbolsOffset]);
                }
            }
        }
        return result;
    }

    public getAllHiddenCoordinatesWithState(state:T):[number, number][] {
        return this.getCoordinatesWithStateBeforeVisible(state).concat(this.getCoordinatesWithStateAfterVisible(state));
    }

    /**
     * Creates a matrix of booleans for all visible states.
     * true for those coordinates that match the state argument and false otherwise.
     *
     * @param state The state to filter.
     * @returns {[boolean][]}
     */
    public getVisibleMatrixWithState(state:T):boolean[][] {
        let result:boolean[][] = [];

        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            result[reelIndex] = [];
            for(let symbolIndex = 0; symbolIndex < this._visibleSymbolsLength; symbolIndex++) {
                result[reelIndex][symbolIndex] = this.getStateAt(reelIndex, symbolIndex) == state;
            }
        }
        return result;
    }


    //Get functions that return actual states

    public getRawMatrix():T[][] {
        return this._stateMatrix;
    }

    public getVisibleMatrix():T[][] {
        let result:T[][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            result[reelIndex] = this._stateMatrix[reelIndex].slice(this._visibleSymbolsOffset, this._visibleSymbolsOffset + this._visibleSymbolsLength);
        }
        return result;
    }

    public getMatrixBeforeVisible():T[][] {
        let result:T[][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            result[reelIndex] = this._stateMatrix[reelIndex].slice(0, this._visibleSymbolsLength);
        }
        return result;
    }

    public getMatrixAfterVisible():T[][] {
        let result:T[][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            result[reelIndex] = this._stateMatrix[reelIndex].slice(this._visibleSymbolsOffset + this._visibleSymbolsLength, this._symbols);
        }
        return result;
    }

    public getHiddenMatrix():T[][] {
        let result:T[][] = [];
        for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
            //Before
            result[reelIndex] = this._stateMatrix[reelIndex].slice(0, this._visibleSymbolsLength);
            //After
            result[reelIndex] = result[reelIndex].concat(this._stateMatrix[reelIndex].slice(this._visibleSymbolsOffset + this._visibleSymbolsLength, this._symbols));
        }
        return result;
    }

    public toString():string {
        let result:string = "\n";
        let padding:string = "";
        let paddingLength:number = 0;
        for(let state of this._states) {
            paddingLength = this.stateToString(state).length > paddingLength ? this.stateToString(state).length : paddingLength;
        }
        for(let i:number = 0; i < paddingLength; i++) {
            padding += " ";
        }

        for(let symbolIndex = 0; symbolIndex < this._symbols; symbolIndex++) {
            let hid:string = "Hidden: ";
            let vis:string = "Visible:";
            if(symbolIndex < this._visibleSymbolsOffset || symbolIndex >= this._visibleSymbolsOffset + this._visibleSymbolsLength) {
                result += hid;
            } else {
                result += vis;
            }

            for(let reelIndex = 0; reelIndex < this._stateMatrix.length; reelIndex++) {
                let state:T = this.getStateAtAbsolute(reelIndex, symbolIndex);
                let stateString;
                if(!state) {
                    stateString = (padding + " ").slice(-paddingLength);
                } else {
                    stateString = (padding + this.stateToString(state)).slice(-paddingLength);
                }

                result += "[" + stateString + "]\t";
            }
            result += "\n";
        }

        return result;
    }

    public stateToString(state:T):string {
        let stateString = state.toString();

        if(state instanceof PIXI.Point) {
            stateString = "" + state.x + ", " + state.y;
        }

        return stateString;

    }


}